import { useTranslation } from 'react-i18next';
import { formatGatewayDate } from '../utils/format';
import type { HealthState } from '../types/shared';

export interface HealthIndicatorProps {
  state: HealthState;
  latencyMs?: number;
  lastCheckedAt?: string;
  size?: 'xs' | 'sm' | 'md';
  showLabel?: boolean;
}

const STATE_ICONS: Record<HealthState, { icon: string; color: string }> = {
  healthy: { icon: '●', color: 'var(--color-healthy)' },
  unhealthy: { icon: '✗', color: 'var(--color-unhealthy)' },
  unknown: { icon: '○', color: 'var(--color-muted)' },
  checking: { icon: '◌', color: 'var(--color-warning)' },
  draining: { icon: '◐', color: 'var(--color-warning)' },
};

const SIZE_MAP = {
  xs: { fontSize: 'var(--font-size-xs)', iconSize: '10px' },
  sm: { fontSize: 'var(--font-size-sm)', iconSize: '12px' },
  md: { fontSize: 'var(--font-size-base)', iconSize: '14px' },
};

export function HealthIndicator({
  state,
  latencyMs,
  lastCheckedAt,
  size = 'sm',
  showLabel = false,
}: HealthIndicatorProps) {
  const { t } = useTranslation();
  const config = STATE_ICONS[state];
  const sizeStyle = SIZE_MAP[size];
  const label = t(`gateway.shared.health.${state}`);

  const parts: string[] = [label];
  if (latencyMs !== undefined) parts.push(`${latencyMs}ms`);
  if (lastCheckedAt) parts.push(formatGatewayDate(lastCheckedAt));

  const ariaLabel = parts.join(', ');

  return (
    <span
      role="img"
      aria-label={ariaLabel}
      title={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-1)',
        fontSize: sizeStyle.fontSize,
        color: config.color,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          fontSize: sizeStyle.iconSize,
          color: config.color,
          lineHeight: 1,
        }}
      >
        {config.icon}
      </span>
      {showLabel && (
        <span style={{ color: config.color }}>
          {label}
          {latencyMs !== undefined && ` (${latencyMs}ms)`}
          {lastCheckedAt && ` · ${formatGatewayDate(lastCheckedAt)}`}
        </span>
      )}
    </span>
  );
}
